<template>
  <div class="payment-sheet grid grid-rows-[1fr_auto] w-full h-full overflow-hidden">
    <!-- ── Manual (card-present) charge — the staff member types the card here ── -->
    <!-- Rendered as a step of this sheet rather than a third stacked drawer, which
         IOSDrawer doesn't support. -->
    <div v-if="manualCardUuid" class="min-h-0 w-full overflow-hidden">
      <ManualCardChargeForm
        class="p-0"
        :paymentUuid="manualCardUuid"
        @completed="close(true)" />
    </div>

    <!-- ── Charge on a physical PayMe terminal — waits for the card ── -->
    <div v-else-if="posCharge" class="min-h-0 w-full overflow-y-auto">
      <PosChargeWaiting
        :paymentUuid="posCharge.uuid"
        :posId="posCharge.posId"
        :terminalName="posCharge.terminalName"
        :amount="posCharge.amount"
        @completed="close(true)"
        @back="posCharge = undefined" />
    </div>

    <!-- Scrollable region -->
    <div v-else class="overflow-y-auto min-h-0 w-full pb-2">
      <!-- ── Missing final prices step ── -->
      <div v-if="showHowMuchToPayModal" class="flex flex-col gap-4 py-2">
        <div>
          <div class="text-base font-semibold" style="color:#18181b">חסרים לנו כמה פרטים</div>
          <div class="text-[13px] mt-1" style="color:#71717a">יש בתור הזה שירותים שדורשים קביעת מחיר סופי לפני מעבר לתשלום</div>
        </div>
        <div v-for="service of howMuchToPayModalServices" :key="service.id" class="flex flex-col gap-1.5">
          <span class="text-[13px] font-medium" style="color:#27272a">כמה סה״כ לגבות עבור {{ service.name }}?</span>
          <div class="w-full relative">
            <input-number
              v-model="howMuchToPayModalOverrides[service.id]"
              class="w-full"
              inputClass="w-full"
              :placeholder="service.kind === 'from' ? `החל מ-${service.minPrice.toFixed(2)}` : `${service.minPrice.toFixed(2)} - ${service.maxPrice.toFixed(2)}`"
              :maxFractionDigits="2"
              :min="0"
              locale="he-IL"
            />
            <span class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm" style="color:#a1a1aa">₪</span>
          </div>
        </div>

        <Button
          :disabled="isHowMuchToPayModalCtaDisabled"
          label="המשך"
          class="w-full"
          @click="showHowMuchToPayModal = false"
        />
      </div>

      <!-- ── Payment options ── -->
      <template v-else>
        <div v-if="loading" class="flex flex-col gap-3 pt-2">
          <Skeleton borderRadius="1rem" height="3.5rem" />
          <Skeleton borderRadius="1rem" height="3.5rem" />
          <Skeleton borderRadius="1rem" height="3.5rem" />
        </div>

        <div v-else class="flex flex-col">
          <!-- Payment methods -->
          <div class="section-label first">אופן התשלום</div>
          <div class="method-card">
            <div v-for="(m, i) in paymentMethods" :key="m.id" class="method-row" :class="{ last: i === paymentMethods.length - 1 }">
              <div class="method-head" :class="{ on: m.selected }" @click="onMethodClick(m)">
                <div class="tile" :style="{ background: m.tint }"><i :class="['pi', m.icon]" :style="{ color: m.fg }" /></div>
                <div class="grow min-w-0">
                  <div class="m-title" :class="{ sel: m.selected }">{{ m.title }}</div>
                  <div class="m-sub">{{ m.sub }}</div>
                </div>
                <span class="radio" :class="{ on: m.selected }" />
              </div>

              <!-- invoice: how was it paid -->
              <div v-if="m.expand === 'invoice' && expandedRow === m.id" class="method-panel">
                <div class="ask">איך התבצע התשלום?</div>
                <div class="chips">
                  <div v-for="c in invoiceChips" :key="c.method" class="chip"
                       :class="{ on: selectedPaymentMethod === c.method }"
                       @click="selectedPaymentMethod = c.method">{{ c.label }}</div>
                </div>
              </div>

              <!-- POS: which terminal -->
              <div v-else-if="m.expand === 'pos' && expandedRow === m.id" class="method-panel">
                <div class="ask">באיזה מסוף?</div>
                <div class="chips">
                  <div v-for="t in posTerminals" :key="t.posId" class="chip"
                       :class="{ on: selectedPosId === t.posId }"
                       @click="selectedPosId = t.posId">{{ terminalLabel(t) }}</div>
                </div>
              </div>

              <!-- saved card charge -->
              <div v-else-if="m.expand === 'saved-card' && expandedRow === m.id && savedCardStatus" class="method-panel">
                <div class="text-[12.5px]" style="color:#52525b">חיוב הכרטיס השמור של הלקוח</div>
                <div v-if="(savedCardStatus.alreadyPaid ?? 0) > 0" class="text-[11.5px] mt-1" style="color:#71717a">
                  שולם עד כה: ₪{{ savedCardStatus.alreadyPaid }} (מקדמה)
                </div>
                <div class="text-[11.5px] mt-1" style="color:#71717a">
                  {{ savedCardStatus.maxAmount != null ? `ניתן לחייב עד ₪${savedCardStatus.maxAmount}` : 'שירות במחיר פתוח — קבעו את הסכום לחיוב' }}
                </div>
                <input-number
                  v-model="chargeAmount"
                  :min="1"
                  :max="savedCardStatus.maxAmount ?? undefined"
                  :maxFractionDigits="2"
                  mode="currency"
                  currency="ILS"
                  locale="he-IL"
                  class="w-full mt-2"
                  inputClass="w-full" />
                <div v-if="useCustomerBalance && availableCredit > 0 && chargeAmount" class="text-[11.5px] mt-1" style="color:#15803d">
                  קיזוז {{ ils(Math.min(availableCredit, chargeAmount)) }} מהיתרה · לחיוב בכרטיס: {{ ils(Math.max(0, chargeAmount - availableCredit)) }}
                </div>
                <div v-if="savedCardStatus.tokenExpiresAt" class="text-[11px] mt-1" style="color:#a1a1aa">
                  בתוקף עד {{ formatExpiry(savedCardStatus.tokenExpiresAt) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Debt: how much to collect now -->
          <template v-if="payFor === 'debt'">
            <div class="section-label">סכום לתשלום</div>
            <div class="flex flex-col gap-1">
              <input-number
                v-model="debtPayAmount"
                :min="0"
                :max="debtAmount"
                :maxFractionDigits="2"
                mode="currency"
                currency="ILS"
                locale="he-IL"
                class="w-full"
                inputClass="w-full" />
              <span class="text-[11.5px]" style="color:#71717a">סך החוב: {{ ils(debtAmount) }} · ניתן לגבות גם חלק ממנו</span>
            </div>
          </template>

          <!-- Discounts & redemptions -->
          <template v-if="discountMethods.length">
            <div class="section-label">הטבות ומימושים</div>
            <div class="method-card">
              <div v-for="(m, i) in discountMethods" :key="m.id" class="method-row" :class="{ last: i === discountMethods.length - 1 }">
                <div class="method-head" :class="{ on: m.selected }" @click="onDiscountClick(m)">
                  <div class="tile" :style="{ background: m.tint }"><i :class="['pi', m.icon]" :style="{ color: m.fg }" /></div>
                  <div class="grow min-w-0">
                    <div class="m-title" :class="{ sel: m.selected }">{{ m.title }}</div>
                    <div class="m-sub">{{ m.sub }}</div>
                  </div>
                  <span class="radio" :class="{ on: m.selected }" />
                </div>
                <div v-if="expandedRow === m.id" class="method-panel">
                  <div class="ask">{{ m.ask }}</div>
                  <div v-if="m.items.length" class="pick-list">
                    <div v-for="it in m.items" :key="it.id" class="pick-item" :class="{ on: it.on }" @click="it.pick()">
                      <span class="pick-radio" :class="{ on: it.on }" />
                      <div class="grow min-w-0">
                        <div class="pick-name">{{ it.name }}</div>
                        <div v-if="it.note" class="pick-note">{{ it.note }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-box">
                    <i class="pi pi-info-circle" style="color:#a1a1aa;font-size:14px;margin-top:1px;flex:none" />
                    <div>
                      <div class="empty-title">{{ m.empty }}</div>
                      <div v-if="m.emptyHint" class="empty-hint">{{ m.emptyHint }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Pay for products -->
          <div v-if="showPayForCart" class="mt-3">
            <settings-toggle
              :status="isPayForProducts ?? false"
              title="לשלוח בחשבונית גם על המוצרים"
              @value-changed="(val) => { isPayForProducts = val; }" />
          </div>

          <!-- Price editor (visit only) -->
          <template v-if="canEditPrice">
            <div class="section-label">מחיר</div>
            <div class="price-card" :class="{ open: showPriceEditor }">
              <div class="price-head" @click="showPriceEditor = !showPriceEditor">
                <div class="price-tile"><i class="pi pi-pencil" /></div>
                <div class="grow min-w-0">
                  <div class="flex items-baseline gap-2 flex-wrap">
                    <div class="p-title">שינוי מחיר לתור זה</div>
                    <span class="gross" :class="{ changed: priceChanged }">{{ ils(grossTotal) }}</span>
                    <span v-if="priceChanged" class="list-strike">{{ ils(listTotal) }}</span>
                  </div>
                  <div class="price-sub" :class="{ changed: priceChanged }">
                    {{ priceChanged ? 'מחיר מותאם לתור זה' : 'כרגע לפי המחירון' }}
                  </div>
                </div>
                <i :class="['pi', showPriceEditor ? 'pi-chevron-down' : 'pi-chevron-left']" class="chev" />
              </div>
              <div v-if="showPriceEditor" class="price-body">
                <div v-for="(s, i) in editablePriceServices" :key="s.id" class="price-row" :class="{ last: i === editablePriceServices.length - 1 }">
                  <div class="grow min-w-0">
                    <div class="pname">{{ s.name }}</div>
                    <div class="pmeta">
                      <span class="list">מחירון {{ ils(s.list) }}</span>
                      <span v-if="isOverridden(s)" class="reset" @click="resetPrice(s.id)">
                        <i class="pi pi-undo" /> איפוס
                      </span>
                    </div>
                  </div>
                  <div class="price-input" :class="{ overridden: isOverridden(s) }">
                    <span class="cur">₪</span>
                    <input-number
                      v-model="howMuchToPayModalOverrides[s.id]"
                      :placeholder="String(s.list)"
                      :min="0"
                      :maxFractionDigits="2"
                      :useGrouping="false"
                      locale="he-IL"
                      :inputStyle="priceInputStyle(s)" />
                  </div>
                </div>
                <div class="info-callout">
                  <i class="pi pi-info-circle" />
                  <span>המחיר שתגדירו כאן תקף לתור זה בלבד ולא ישנה את המחירון.</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <!-- Footer (options mode only) -->
    <div v-if="!showHowMuchToPayModal && !manualCardUuid && !posCharge" class="w-full">
      <div v-if="payFor === 'debt'" class="visit-total">
        <div class="total-row">
          <span class="total-label">סה״כ לתשלום</span>
          <span class="total-amt">{{ ils(debtPayAmount ?? 0) }}</span>
        </div>
      </div>
      <div v-else-if="payFor === 'visit' && paymentPreview" class="visit-total">
        <div v-if="breakdownEntries.length" class="breakdown">
          <div v-for="[key, val] in breakdownEntries" :key="key" class="bd-row">
            <span class="bd-label">{{ translateDiscountMethod(key as keyof IPaymentReviewCalculationResponse['breakdown']) }}</span>
            <span class="bd-amt" :class="{ neg: key !== 'products' }">{{ key === 'products' ? '' : '−' }}₪{{ Math.abs(val.amount).toFixed(2) }}</span>
          </div>
        </div>
        <div class="total-row">
          <span class="total-label">סה״כ לתשלום</span>
          <span v-if="showListStrike" class="total-strike">{{ ils(listTotal) }}</span>
          <span class="total-amt" :class="{ changed: totalChanged }">{{ ils(paymentPreview.finalAmount) }}</span>
        </div>
      </div>
      <drawer-footer
        :approveDisable="approveDisabled"
        :approveLoading="isPaymentButtonLoading"
        :approveLabel="approveLabel"
        cancelLabel="ביטול"
        @approve-click="sendPaymentToCustomer"
        @cancel-click="$emit('close')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import { CustomersApi, PaymentsApi } from '@app/api/admin';
import { ISavedCardStatusResponse } from '@app/api/admin/payments-api';
import PosChargeWaiting from '@app/components/admin/modals/PosChargeWaiting.vue';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { ProductOrdersApi } from '@app/api/admin/product-orders-api';
import {
  IAvailableBundlePackResponse,
  IAvailableVouchersResponse,
  ICalendarEventDetails,
  ICreateServicePayment,
  IPaymentReviewCalculationResponse,
  LoyaltyProgramPromosTypes,
  PaymentMethod,
  PaymentProvider,
  PaymeSettings,
  type IPaymePosTerminal,
  VoucherTypes
} from '@torim-app/types';
import { useAdminStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import { isFromPrice } from '@torim-app/utils';
import ManualCardChargeForm from '@app/components/admin/components/ManualCardChargeForm.vue';

interface PaymentRow {
  id: string;
  title: string;
  sub: string;
  icon: string;
  tint: string;
  fg: string;
  expand?: 'invoice' | 'saved-card' | 'pos';
  selected: boolean;
  select: () => void;
}
interface DiscountItem { id: string; name: string; note?: string; on: boolean; pick: () => void; }
interface DiscountRow {
  id: string;
  title: string;
  sub: string;
  icon: string;
  tint: string;
  fg: string;
  ask: string;
  selected: boolean;
  items: DiscountItem[];
  empty?: string;
  emptyHint?: string;
}

const emit = defineEmits(['close']);
const props = defineProps({
  uuid: { type: String, required: true },
  payFor: {
    // 'debt': `uuid` is the customer id and `debtAmount` what they owe
    type: String as () => 'visit' | 'voucher' | 'bundle-pack' | 'product-order' | 'debt',
    required: true
  },
  debtAmount: { type: Number, required: false, default: 0 },
  event: {
    type: Object as PropType<ICalendarEventDetails>,
    required: false,
    default: () => ({})
  },
  voucherServices: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => ([])
  },
  hasVoucherSupport: { type: Boolean, required: false, default: false },
  hasBundleSupport: { type: Boolean, required: false, default: false }
});
const toast = useToast();
const store = useAdminStore();

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const showHowMuchToPayModal = ref(false);
const howMuchToPayModalServices = ref<Array<{ id: string; name: string; minPrice: number; maxPrice: number; kind: 'range' | 'from' }>>([]);
const howMuchToPayModalOverrides = reactive<Record<string, number>>({});

// Inline, opt-in per-service price editing for a visit. Writes to the same
// overridePrices map the backend already understands — an empty field means
// "use the list price" (no override sent), so the untouched common case is unchanged.
const showPriceEditor = ref(false);
const editablePriceServices = ref<Array<{ id: string; name: string; list: number; placeholder: string; required: boolean }>>([]);

// Which method/discount row is currently expanded (only one at a time).
const expandedRow = ref<string | null>(null);

const loading = ref(false);
const selectedPaymentMethod = ref<PaymentMethod>();
const skipPayment = ref(false);
const isPaymentButtonLoading = ref(false);
const isPayForProducts = ref(true);
const availableVouchers = ref<IAvailableVouchersResponse[]>([]);
// Only vouchers whose service matches the visit's services (backend sets isApplicable).
const applicableVouchers = computed(() => availableVouchers.value.filter(v => v.isApplicable));
const selectedVoucher = ref<{ id: string; title: string }>();
const availableBundlePacks = ref<IAvailableBundlePackResponse[]>([]);
const selectedBundlePack = ref<{ id: string; title: string }>();
const lpResponse = ref<{ type: 'points' | 'discount'; value: number }>({ type: 'points', value: 0 });
const useLoyaltyBenefits = ref<{ use: boolean; selectedBenefit: string }>();
const paymentPreview = ref<IPaymentReviewCalculationResponse>();
// Saved-card (delayed charge) support — populated when the appointment has a stored token.
const savedCardStatus = ref<ISavedCardStatusResponse>();
const chargeSavedCardSelected = ref(false);
const chargeAmount = ref<number>();
// Customer credit (יתרה) — offered as a discount on a visit when the customer has some.
const availableCredit = ref(0);
const useCustomerBalance = ref(false);
// Debt payment: how much of the debt to collect now (defaults to all of it).
const debtPayAmount = ref<number | null>(props.debtAmount > 0 ? props.debtAmount : null);

// Which flavour of a credit payment the owner picked. All three are PaymentMethod.Credit;
// they differ only in who fills the card form and whether the card is charged now.
type CreditMode = 'link' | 'manual' | 'collect-card' | 'pos';
const creditMode = ref<CreditMode>('link');
// Set once a manual (card-present) request exists — the sheet then shows the card form.
const manualCardUuid = ref<string>();
// Set once a request was sent to a PayMe terminal — the sheet then waits for the card.
const posCharge = ref<{ uuid: string; posId: string; terminalName?: string; amount?: number }>();

const hasPayment = computed(() => store.businessSiteData?.hasPayments ?? false);
const hasInvoices = computed(() => store.businessSiteData?.hasInvoices ?? false);
const showPayForCart = computed(() => props.event?.cartItems && props.event.cartItems?.length > 0 && hasPayment.value);
const hasPayme = computed(() => store.getBusinessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME);
// PayMe businesses with Bit enabled can text the customer a Bit payment request.
const hasBitRequest = computed(() => hasPayme.value
  && !!(store.getBusinessSiteData?.paymentIntegration?.paymentConfig?.settings as PaymeSettings | undefined)?.paymentMethods?.bit);
const isBitRequest = computed(() => hasBitRequest.value && selectedPaymentMethod.value === PaymentMethod.Bit);
// Businesses configured to only COLLECT the card at booking (no charge) can also ask for it
// after the fact — the link stores the card and the charge is made from here.
const isCollectCardBusiness = computed(() =>
  hasPayme.value && store.getBusinessSiteData?.configurations?.scheduleChargeMode === 'save-card');

// Physical PayMe card terminals, set by hand on the business's PayMe settings.
const posTerminals = computed<IPaymePosTerminal[]>(() => hasPayme.value
  ? ((store.getBusinessSiteData?.paymentIntegration?.paymentConfig?.settings as PaymeSettings | undefined)?.posTerminals ?? [])
  : []);
const selectedPosId = ref<string>();
const terminalLabel = (t: IPaymePosTerminal) => t.name || `מסוף ${t.posId}`;

const isCreditSelected = computed(() => selectedPaymentMethod.value === PaymentMethod.Credit);
const isManualCard = computed(() => isCreditSelected.value && creditMode.value === 'manual');
const isCollectCard = computed(() => isCreditSelected.value && creditMode.value === 'collect-card');
const isPosCharge = computed(() => isCreditSelected.value && creditMode.value === 'pos');
// Requests created for the card form or the terminal skip the SMS / WhatsApp link.
const skipsLink = computed(() => isManualCard.value || isPosCharge.value);

const selectCreditMode = (mode: CreditMode) => {
  selectedPaymentMethod.value = PaymentMethod.Credit;
  creditMode.value = mode;
};

const INVOICE_METHODS = [PaymentMethod.Cash, PaymentMethod.BankTransfer, PaymentMethod.PayBox, PaymentMethod.Bit];
const invoiceChips = computed(() => {
  const chips = [
    { method: PaymentMethod.Cash, label: 'מזומן' },
    { method: PaymentMethod.BankTransfer, label: 'העברה בנקאית' }
  ];
  if (!hasPayme.value) {
    chips.push({ method: PaymentMethod.PayBox, label: 'פייבוקס' });
    chips.push({ method: PaymentMethod.Bit, label: 'ביט' });
  }
  return chips;
});

// ── payment method rows ──────────────────────────────────────────────
const paymentMethods = computed<PaymentRow[]>(() => {
  const rows: PaymentRow[] = [];
  if (hasPayment.value)
    rows.push({
      id: 'link', title: 'שליחת קישור תשלום', sub: 'הלקוח משלם בעצמו בנייד',
      icon: 'pi-credit-card', tint: '#edeafd', fg: '#6831d0',
      selected: isCreditSelected.value && creditMode.value === 'link',
      select: () => selectCreditMode('link')
    });
  // Card-present charge: the employee hands over the phone / reads the card over the phone.
  // Visits and debt payments — the endpoints that can create the request without texting the link.
  if ((props.payFor === 'visit' || props.payFor === 'debt') && hasPayment.value && hasPayme.value)
    rows.push({
      id: 'manual-card', title: 'חיוב ידני בכרטיס אשראי', sub: 'הזנת פרטי הכרטיס כאן, ללא שליחת קישור',
      icon: 'pi-mobile', tint: '#e0f2fe', fg: '#0369a1',
      selected: isManualCard.value,
      select: () => selectCreditMode('manual')
    });
  // Card-present on a physical terminal: the request goes to the terminal, which asks for the card.
  if ((props.payFor === 'visit' || props.payFor === 'debt') && hasPayment.value && posTerminals.value.length)
    rows.push({
      id: 'pos', title: 'חיוב במסוף אשראי',
      sub: posTerminals.value.length === 1
        ? `הלקוח מעביר כרטיס ב${terminalLabel(posTerminals.value[0])}`
        : 'הלקוח מעביר כרטיס במסוף של העסק',
      icon: 'pi-credit-card', tint: '#fce7f3', fg: '#be185d',
      expand: posTerminals.value.length > 1 ? 'pos' : undefined,
      selected: isPosCharge.value,
      select: () => {
        selectCreditMode('pos');
        if (!posTerminals.value.some(t => t.posId === selectedPosId.value)) selectedPosId.value = posTerminals.value[0]?.posId;
      }
    });
  if (hasBitRequest.value)
    rows.push({
      id: 'bit', title: 'שליחת בקשת תשלום בביט', sub: 'הלקוח יקבל הודעה עם קישור לתשלום בביט',
      icon: 'pi-mobile', tint: '#e0f7f4', fg: '#0f766e',
      selected: isBitRequest.value,
      select: () => { selectedPaymentMethod.value = PaymentMethod.Bit; }
    });
  // Collect-only businesses: send a link that stores the card without charging it.
  if (props.payFor === 'visit' && isCollectCardBusiness.value && !savedCardStatus.value?.hasToken)
    rows.push({
      id: 'collect-card', title: 'שליחת קישור לאיסוף פרטי אשראי', sub: 'הכרטיס נשמר ללא חיוב; החיוב מתבצע מכאן',
      icon: 'pi-id-card', tint: '#fef3c7', fg: '#b45309',
      selected: isCollectCard.value,
      select: () => selectCreditMode('collect-card')
    });
  if (hasInvoices.value)
    rows.push({
      id: 'invoice', title: 'שליחת חשבונית', sub: 'חשבונית מס / קבלה במייל',
      icon: 'pi-receipt', tint: '#dbeafe', fg: '#1d4ed8', expand: 'invoice',
      // The chips shown, not INVOICE_METHODS: on PayMe, Bit is a payment request (its own row)
      selected: invoiceChips.value.some(c => c.method === selectedPaymentMethod.value),
      select: () => { if (!invoiceChips.value.some(c => c.method === selectedPaymentMethod.value)) selectedPaymentMethod.value = PaymentMethod.Cash; }
    });
  if (props.payFor === 'product-order' && !hasInvoices.value)
    rows.push({
      id: 'product-cash', title: 'שולם בבית העסק (מזומן)', sub: 'רישום תשלום במזומן, ללא חשבונית',
      icon: 'pi-wallet', tint: '#dcfce7', fg: '#15803d',
      selected: selectedPaymentMethod.value === PaymentMethod.Cash,
      select: () => { selectedPaymentMethod.value = PaymentMethod.Cash; }
    });
  if (props.payFor === 'bundle-pack')
    rows.push({
      id: 'skip', title: 'ללא תשלום', sub: 'הפעלת הכרטיסייה מיידית',
      icon: 'pi-ban', tint: '#f4f4f5', fg: '#71717a',
      selected: skipPayment.value,
      select: () => { skipPayment.value = true; selectedPaymentMethod.value = undefined; }
    });
  if (props.payFor === 'visit' && savedCardStatus.value?.hasToken)
    rows.push({
      id: 'saved-card', title: 'חיוב כרטיס שמור', sub: 'חיוב מיידי של הכרטיס השמור',
      icon: 'pi-credit-card', tint: '#edeafd', fg: '#6831d0', expand: 'saved-card',
      selected: chargeSavedCardSelected.value,
      select: () => { chargeSavedCardSelected.value = true; selectedPaymentMethod.value = undefined; skipPayment.value = false; }
    });
  return rows;
});

const onMethodClick = (m: PaymentRow) => {
  m.select();
  expandedRow.value = m.expand ? m.id : null;
};

// ── discount / redemption rows ───────────────────────────────────────
const selectVoucher = (v: IAvailableVouchersResponse) => {
  if (selectedVoucher.value?.id === v.id) { selectedVoucher.value = undefined; return; }
  selectedVoucher.value = { id: v.id, title: generateVoucherTitle(v) };
  selectedBundlePack.value = undefined;
  useLoyaltyBenefits.value = undefined;
};
const selectBundle = (p: IAvailableBundlePackResponse) => {
  if (selectedBundlePack.value?.id === p.id) { selectedBundlePack.value = undefined; return; }
  selectedBundlePack.value = { id: p.id, title: generateBundlePackTitle(p) };
  selectedVoucher.value = undefined;
  useLoyaltyBenefits.value = undefined;
};
const toggleLoyalty = () => {
  if (useLoyaltyBenefits.value?.use) { useLoyaltyBenefits.value = undefined; return; }
  useLoyaltyBenefits.value = { use: true, selectedBenefit: getBenefitDescription().title };
  selectedVoucher.value = undefined;
  selectedBundlePack.value = undefined;
};

// Customer credit (יתרה) — every purchase type can use it (not a debt payment).
const balanceRow = computed<DiscountRow | null>(() => {
  if (availableCredit.value <= 0 || props.payFor === 'debt') return null;
  return {
    id: 'balance', title: 'שימוש ביתרת הלקוח', sub: `יתרה זמינה ${ils(availableCredit.value)}`,
    icon: 'pi-wallet', tint: '#dcfce7', fg: '#15803d', ask: 'קיזוז מהיתרה',
    selected: useCustomerBalance.value,
    items: [{
      id: 'use-balance', name: `קיזוז עד ${ils(availableCredit.value)} מהסכום לתשלום`,
      note: props.payFor === 'visit' ? 'היתרה תקוזז רק לאחר שהתשלום יושלם' : 'היתרה תקוזז רק לאחר שהתשלום יושלם. אם היתרה מכסה את כל הסכום — הרכישה תאושר מיד',
      on: useCustomerBalance.value, pick: () => { useCustomerBalance.value = !useCustomerBalance.value; }
    }]
  };
});

const discountMethods = computed<DiscountRow[]>(() => {
  if (props.payFor !== 'visit') return balanceRow.value ? [balanceRow.value] : [];
  const rows: DiscountRow[] = [];
  if (props.hasVoucherSupport)
    rows.push({
      id: 'voucher', title: 'תשלום באמצעות שובר', sub: 'מימוש שובר או קוד הנחה',
      icon: 'pi-ticket', tint: '#fff1d6', fg: '#b5580b', ask: 'בחרו שובר',
      selected: !!selectedVoucher.value,
      empty: 'אין שוברים מתאימים לשירות',
      items: applicableVouchers.value.map(v => ({
        id: v.id, name: generateVoucherTitle(v), on: selectedVoucher.value?.id === v.id, pick: () => selectVoucher(v)
      }))
    });
  if (props.hasBundleSupport)
    rows.push({
      id: 'bundle', title: 'תשלום בכרטיסייה', sub: 'ניקוב כרטיסייה פעילה',
      icon: 'pi-id-card', tint: '#dcfce7', fg: '#15803d', ask: 'בחרו כרטיסייה',
      selected: !!selectedBundlePack.value,
      empty: 'אין כרטיסיות פעילות ללקוח',
      items: availableBundlePacks.value.map(p => ({
        id: p.id, name: generateBundlePackTitle(p), on: selectedBundlePack.value?.id === p.id, pick: () => selectBundle(p)
      }))
    });
  if (store.getBusinessSiteData?.loyaltyProgram) {
    const benefit = getBenefitDescription();
    rows.push({
      id: 'loyalty', title: 'תשלום במועדון לקוחות', sub: 'שימוש בהטבות ובנקודות שנצברו',
      icon: 'pi-sparkles', tint: '#fce7f3', fg: '#be185d', ask: 'בחרו הטבה',
      selected: !!useLoyaltyBenefits.value?.use,
      empty: 'אין הטבות זמינות ללקוח זה',
      items: lpResponse.value?.value
        ? [{ id: 'benefit', name: benefit.title, note: benefit.description, on: !!useLoyaltyBenefits.value?.use, pick: () => toggleLoyalty() }]
        : []
    });
  }
  if (balanceRow.value) rows.push(balanceRow.value);
  return rows;
});

const onDiscountClick = (m: DiscountRow) => {
  expandedRow.value = expandedRow.value === m.id ? null : m.id;
};

// ── price editor helpers ─────────────────────────────────────────────
const priceOfService = (s: { id: string; list: number }) => {
  const o = howMuchToPayModalOverrides[s.id];
  return typeof o === 'number' ? o : s.list;
};
const grossTotal = computed(() => editablePriceServices.value.reduce((sum, s) => sum + priceOfService(s), 0));
const listTotal = computed(() => editablePriceServices.value.reduce((sum, s) => sum + s.list, 0));
const priceChanged = computed(() => Math.round(grossTotal.value * 100) !== Math.round(listTotal.value * 100));
const isOverridden = (s: { id: string; list: number }) => {
  const o = howMuchToPayModalOverrides[s.id];
  return typeof o === 'number' && o !== s.list;
};
const resetPrice = (id: string) => { howMuchToPayModalOverrides[id] = null as unknown as number; };
const priceInputStyle = (s: { id: string; list: number }) => ({
  width: '100%', boxSizing: 'border-box', padding: '9px 26px 9px 10px', fontSize: '14px', fontWeight: 600,
  textAlign: 'right', direction: 'ltr', borderRadius: '10px',
  color: isOverridden(s) ? '#6831d0' : '#18181b',
  background: isOverridden(s) ? '#f5f4fe' : '#fff',
  border: `1.5px solid ${isOverridden(s) ? '#ddd8fc' : '#e4e4e7'}`
});

const isHowMuchToPayModalCtaDisabled = computed(() => {
  for (const { id } of howMuchToPayModalServices.value)
    if (!howMuchToPayModalOverrides[id]) return true;
  return false;
});

// Only genuinely-entered numbers count as overrides. A cleared InputNumber sets the
// value to null (keeping the key), which must NOT flip the backend's hasOverrides flag.
const overridePricesPayload = computed<Record<string, number>>(() => {
  const out: Record<string, number> = {};
  for (const [id, val] of Object.entries(howMuchToPayModalOverrides))
    if (typeof val === 'number') out[id] = val;
  return out;
});

// from/range services must have a concrete price before the backend accepts the request.
const requiredPriceMissing = computed(() =>
  editablePriceServices.value.some(s => s.required && !howMuchToPayModalOverrides[s.id]));

const canEditPrice = computed(() =>
  props.payFor === 'visit' && editablePriceServices.value.length > 0 && !chargeSavedCardSelected.value);

// ── footer total ─────────────────────────────────────────────────────
const breakdownEntries = computed<Array<[string, { amount: number }]>>(() =>
  paymentPreview.value ? (Object.entries(paymentPreview.value.breakdown) as Array<[string, { amount: number }]>) : []);
const hasCredit = computed(() => breakdownEntries.value.some(([k]) => k !== 'products'));
const totalChanged = computed(() => priceChanged.value || hasCredit.value);
const showListStrike = computed(() => props.payFor === 'visit' && !!paymentPreview.value && priceChanged.value);

// The customer's credit covers the whole visit: the chosen payment method no longer matters —
// approving settles it directly (no link, no card form).
const coveredByBalance = computed(() =>
  props.payFor === 'visit' && useCustomerBalance.value && !chargeSavedCardSelected.value
  && !!paymentPreview.value && paymentPreview.value.finalAmount <= 0 && !!paymentPreview.value.breakdown?.balance);

const approveDisabled = computed(() => {
  if (coveredByBalance.value) return requiredPriceMissing.value;
  if (chargeSavedCardSelected.value)
    return !chargeAmount.value || chargeAmount.value <= 0 || chargeAmount.value > (savedCardStatus.value?.maxAmount ?? Infinity);
  // Nothing to collect or store a card for when the visit comes out free
  if (props.payFor === 'visit' && (isManualCard.value || isCollectCard.value || isPosCharge.value) && (paymentPreview.value?.finalAmount ?? 0) <= 0) return true;
  if (isPosCharge.value && !selectedPosId.value) return true;
  const nothingSelected = !selectedPaymentMethod.value && !skipPayment.value;
  if (props.payFor === 'debt')
    return nothingSelected || !debtPayAmount.value || debtPayAmount.value <= 0 || debtPayAmount.value > props.debtAmount;
  // For pack/voucher there is no payment preview, so require an explicit choice
  // (a payment method, or the "ללא" skip option when available) before enabling.
  if (props.payFor !== 'visit') return nothingSelected;
  if (requiredPriceMissing.value) return true;
  return nothingSelected && (paymentPreview.value?.finalAmount ?? 0) > 0;
});

const approveLabel = computed(() => {
  if (coveredByBalance.value) return 'אישור — כיסוי מלא מהיתרה';
  if (isManualCard.value) return 'המשך להזנת כרטיס';
  if (isPosCharge.value) return 'שליחה למסוף';
  if (isBitRequest.value) return 'שליחת בקשה בביט';
  if (isCollectCard.value) return 'שליחת קישור';
  return 'אישור';
});

const formatExpiry = (iso: string) => DateTime.fromISO(iso).toFormat('dd/MM/yyyy');

const translateDiscountMethod = (key: keyof IPaymentReviewCalculationResponse['breakdown']): string => {
  return {
    products: 'מוצרים',
    loyalty: 'הטבת מועדון לקוחות',
    advancedPayment: 'תשלום מקדמה',
    voucher: 'שובר',
    bundlePack: 'כרטיסייה',
    coupon: 'קופון',
    balance: 'קיזוז מיתרת הלקוח'
  }[key] ?? key;
};

const generateVoucherTitle = (voucher: IAvailableVouchersResponse) => {
  if (voucher.voucherType === VoucherTypes.AMOUNT)
    return `שובר הנחה על סך ${voucher.amount} ש"ח`;
  const services = store.getServices.filter(x => voucher.serviceId!.includes(x.id));
  return `שובר ${voucher.serviceDiscount}% הנחה ל${services.map(x => x.name).join(', ')}`;
};

const generateBundlePackTitle = (pack: IAvailableBundlePackResponse) =>
  `${pack.templateName} (${pack.remainingUses}/${pack.totalUses})`;

const getBenefitDescription = () => {
  const loyaltyProgramDetails = store.getBusinessSiteData?.loyaltyProgram;
  if (loyaltyProgramDetails?.promoType === LoyaltyProgramPromosTypes.Discount) {
    const title = loyaltyProgramDetails.discountOptions?.discountAmount === 100
      ? `תור מתנה`
      : `תור ב-${loyaltyProgramDetails.discountOptions?.discountAmount}% הנחה לשירותים נבחרים`;
    return { title, description: `יש ללקוח ${lpResponse.value.value} הטבות` };
  }
  return { title: `שימוש בנקודות`, description: `יש ללקוח ${lpResponse.value.value} נקודות` };
};

const loadPurchaseCredit = async () => {
  if (props.payFor !== 'voucher' && props.payFor !== 'bundle-pack' && props.payFor !== 'product-order') return;
  try {
    const { credit } = await PaymentsApi.getPurchaseCredit(props.payFor, props.uuid);
    availableCredit.value = Math.max(0, credit);
  } catch (e) {
    // No credit option on failure — the purchase itself is unaffected.
    console.error(e);
  }
};

const loadPaymentData = async () => {
  if (props.payFor !== 'visit' || !props.event) return;
  try {
    loading.value = true;
    const { clientId, servicesIds } = props.event;
    const [avVouchers, avBundlePacks, lpResp, savedCard, customerBalance] = await Promise.all([
      PaymentsApi.getAvailableVouchers(clientId, servicesIds.join(',')).catch(() => []),
      PaymentsApi.getAvailableBundlePacks(clientId, servicesIds.join(',')).catch(() => []),
      PaymentsApi.getLoyaltyBenefits(clientId, servicesIds.join(',')).catch(() => ({ type: 'points' as const, value: 0 })),
      PaymentsApi.getSavedCardStatus(props.uuid).catch(() => ({ hasToken: false } as ISavedCardStatusResponse)),
      clientId ? CustomersApi.getBalance(clientId).catch(() => ({ balance: 0, history: [] })) : Promise.resolve({ balance: 0, history: [] })
    ]);
    availableCredit.value = Math.max(0, customerBalance.balance);
    availableVouchers.value = avVouchers;
    availableBundlePacks.value = avBundlePacks;
    lpResponse.value = lpResp;
    if (savedCard.hasToken && savedCard.paymentRequestUuid) {
      savedCardStatus.value = savedCard;
      chargeAmount.value = savedCard.maxAmount;
    }
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את דף התשלום');
  } finally {
    loading.value = false;
  }
};

const getPaymentPreview = async () => {
  if (!props.uuid || props.payFor !== 'visit' || showHowMuchToPayModal.value || requiredPriceMissing.value) return;

  try {
    isPaymentButtonLoading.value = true;
    const extras: ICreateServicePayment['extras'] = {
      isPayForProducts: showPayForCart.value ? isPayForProducts.value : undefined,
      useLoyaltyRewards: useLoyaltyBenefits.value?.use,
      voucherId: selectedVoucher.value?.id,
      bundlePackId: selectedBundlePack.value?.id,
      useCustomerBalance: useCustomerBalance.value || undefined
    };
    paymentPreview.value = await PaymentsApi.previewVisitPayment(props.uuid, {
      overridePrices: overridePricesPayload.value,
      extras
    });
  } catch (e) {
    console.error('Failed to get payment preview', e);
  } finally {
    isPaymentButtonLoading.value = false;
  }
};

// Push a created (unsent) request to the chosen terminal, then wait for the card there.
const sendToTerminal = async (uuid: string, amount?: number) => {
  const terminal = posTerminals.value.find(t => t.posId === selectedPosId.value);
  if (!terminal) return;
  const result = await PaymentsApi.startPosCharge({ paymentRequestUuid: uuid, posId: terminal.posId });
  trackEvent('pos_charge_sent', { payFor: props.payFor, ok: result.success });
  if (!result.success) {
    toast.error(result.error || 'לא הצלחנו לשלוח את החיוב למסוף');
    return;
  }
  posCharge.value = { uuid, posId: terminal.posId, terminalName: terminalLabel(terminal), amount };
};

const sendPaymentToCustomer = async () => {
  trackEvent('payment_sent_to_customer');
  if (!props.uuid || approveDisabled.value) return;

  try {
    isPaymentButtonLoading.value = true;

    // Charge the customer's saved card directly (delayed-charge / save-card flow) —
    // no payment link is sent.
    if (chargeSavedCardSelected.value && props.payFor === 'visit') {
      if (!savedCardStatus.value?.paymentRequestUuid || !chargeAmount.value) return;
      try {
        const chargeResult = await PaymentsApi.chargeSavedCard({
          paymentRequestUuid: savedCardStatus.value.paymentRequestUuid,
          amount: chargeAmount.value,
          useCustomerBalance: useCustomerBalance.value || undefined
        });
        if (chargeResult.success) {
          toast.success(chargeResult.chargedAmount === 0 ? 'התשלום כוסה במלואו מהיתרה' : 'החיוב בוצע בהצלחה');
          close(true);
        } else {
          toast.error(chargeResult.error ?? 'לא הצלחנו לחייב את הכרטיס');
        }
      } catch (chargeError: any) {
        toast.error(chargeError?.error ?? chargeError?.message ?? 'לא הצלחנו לחייב את הכרטיס');
      }
      return;
    }

    // Debt: a general payment flagged to settle the debt once it's paid.
    if (props.payFor === 'debt') {
      const { uuid } = await PaymentsApi.sendGeneralPaymentLink({
        customerId: props.uuid,
        price: debtPayAmount.value!,
        paymentMethod: selectedPaymentMethod.value ?? PaymentMethod.Cash,
        dontSendLink: skipsLink.value,
        settlesCustomerDebt: true,
        note: 'תשלום חוב'
      });
      trackEvent('customer_debt_payment_created', { method: selectedPaymentMethod.value, manual_card: isManualCard.value, pos: isPosCharge.value });
      if (isManualCard.value) {
        manualCardUuid.value = uuid;
        return;
      }
      if (isPosCharge.value) {
        await sendToTerminal(uuid, debtPayAmount.value ?? undefined);
        return;
      }
      toast.success(selectedPaymentMethod.value === PaymentMethod.Credit || isBitRequest.value
        ? 'בקשת התשלום נשלחה, החוב יתעדכן לאחר התשלום'
        : 'התשלום נרשם והחוב עודכן');
      close(true);
      return;
    }

    if (skipPayment.value && props.payFor === 'bundle-pack') {
      await BundlePacksApi.activateBundlePackNoPayment(props.uuid);
      toast.success('הכרטיסייה הופעלה');
      close(true);
      return;
    }

    const extras: ICreateServicePayment['extras'] | undefined = props.payFor === 'visit' ? {
      isPayForProducts: showPayForCart.value ? isPayForProducts.value : undefined,
      useLoyaltyRewards: useLoyaltyBenefits.value?.use,
      voucherId: selectedVoucher.value?.id,
      bundlePackId: selectedBundlePack.value?.id,
      useCustomerBalance: useCustomerBalance.value || undefined
    } : undefined;
    const fullyCovered = coveredByBalance.value;
    const body = {
      // Fully covered by credit: settle as a ₪0 non-credit payment whatever row is selected
      paymentMethod: fullyCovered ? PaymentMethod.Cash : (selectedPaymentMethod.value ?? PaymentMethod.Cash),
      overridePrices: overridePricesPayload.value,
      // Voucher purchases read the credit choice from extras too
      extras: props.payFor === 'voucher' ? { useCustomerBalance: useCustomerBalance.value || undefined } : extras,
      dontSendLink: !fullyCovered && skipsLink.value,
      collectCardOnly: !fullyCovered && isCollectCard.value
    };
    if (props.payFor === 'visit') {
      const result = await PaymentsApi.sendVisitPaymentLink(props.uuid, body);
      if (fullyCovered) {
        toast.success('התשלום כוסה במלואו מהיתרה');
        close(true);
        return;
      }
      // Manual charge: no SMS went out — swap this sheet for the hosted card form and let
      // the customer/employee complete the charge here.
      if (isManualCard.value) {
        if (!result?.uuid) {
          toast.error('לא הצלחנו לפתוח את טופס החיוב');
          return;
        }
        manualCardUuid.value = result.uuid;
        return;
      }
      if (isPosCharge.value) {
        if (!result?.uuid) {
          toast.error('לא הצלחנו ליצור את החיוב');
          return;
        }
        await sendToTerminal(result.uuid, paymentPreview.value?.finalAmount);
        return;
      }
    } else if (props.payFor === 'voucher')
      await PaymentsApi.sendVoucherPaymentLink(props.uuid, body);
    else if (props.payFor === 'bundle-pack')
      await BundlePacksApi.sendBundlePackPaymentLink(props.uuid, { paymentMethod: body.paymentMethod, useCustomerBalance: useCustomerBalance.value || undefined });
    else if (props.payFor === 'product-order')
      await ProductOrdersApi.collectManualSalePayment(props.uuid, body.paymentMethod, useCustomerBalance.value || undefined);

    const nonCreditSuccessMessage = props.payFor === 'bundle-pack'
      ? 'הכרטיסייה עודכנה בהצלחה'
      : props.payFor === 'voucher'
        ? 'השובר עודכן בהצלחה'
        : props.payFor === 'product-order'
          ? 'המכירה נרשמה בהצלחה'
          : 'הביקור עודכן בהצלחה';
    toast.success(
      isBitRequest.value
        ? 'בקשת התשלום בביט נשלחה ללקוח'
        : isCollectCard.value
        ? 'הקישור לאיסוף פרטי האשראי נשלח בהצלחה'
        : selectedPaymentMethod.value === PaymentMethod.Credit
          ? 'בקשת התשלום נשלחה בהצלחה :)'
          : nonCreditSuccessMessage
    );
    close(true);
  } catch (e: any) {
    if (e.status === 'failed') {
      if (e?.message === 'already-exists')
        toast.warning('נראה שכבר נשלחה בקשת תשלום');
      else if (e?.message === 'no-invoices')
        toast.error('נראה שאין לך חיבור לחשבוניות, להוספה פנו אלינו 🙂', { timeout: 3000 });
    } else {
      toast.error('לא הצלחנו לשלוח את בקשת התשלום');
    }
  } finally {
    isPaymentButtonLoading.value = false;
  }
};

const checkMissingPrices = () => {
  if ((!props.event || Object.keys(props.event).length === 0) && !props.voucherServices) return;

  const servicesIds = Object.keys(props.event).length === 0 ? props.voucherServices : props.event.servicesIds;
  const allServices = store.getServices.filter(service => servicesIds.includes(service.id));
  for (const service of allServices) {
    if (isFromPrice(service)) {
      howMuchToPayModalServices.value.push({ id: service.id, name: service.name, minPrice: service.price, maxPrice: service.price, kind: 'from' as const });
    } else if (service.maxPrice && service.maxPrice !== service.price) {
      howMuchToPayModalServices.value.push({ id: service.id, name: service.name, minPrice: service.price, maxPrice: service.maxPrice, kind: 'range' as const });
    }
  }

  if (howMuchToPayModalServices.value.length > 0)
    showHowMuchToPayModal.value = true;
};

// Build the inline price-editor rows for a visit. Does NOT seed overridePrices —
// an untouched row leaves the map empty so the backend keeps the booked subtotal.
const buildEditablePrices = () => {
  if (props.payFor !== 'visit' || !props.event?.servicesIds?.length) return;
  const services = store.getServices.filter(s => props.event.servicesIds.includes(s.id));
  editablePriceServices.value = services.map(s => {
    const fromPrice = isFromPrice(s);
    const isRange = !fromPrice && !!s.maxPrice && s.maxPrice !== s.price;
    return {
      id: s.id,
      name: s.name,
      list: s.price,
      required: fromPrice || isRange,
      placeholder: fromPrice ? `החל מ-₪${s.price}` : isRange ? `₪${s.price}–₪${s.maxPrice}` : `₪${s.price}`
    };
  });
};

const close = (showLoad: boolean) => { emit('close', showLoad); };

onMounted(() => {
  checkMissingPrices();
  buildEditablePrices();
  loadPaymentData();
  loadPurchaseCredit();
});

watch(() => [selectedPaymentMethod.value, selectedVoucher.value, selectedBundlePack.value, useLoyaltyBenefits.value, useCustomerBalance.value, isPayForProducts.value, showHowMuchToPayModal.value, howMuchToPayModalOverrides], () => {
  getPaymentPreview();
}, { immediate: true, deep: true });

watch(selectedPaymentMethod, (val) => {
  if (val) {
    skipPayment.value = false;
    chargeSavedCardSelected.value = false;
  }
  // The credit sub-mode is only meaningful for a credit payment
  if (val !== PaymentMethod.Credit) creditMode.value = 'link';
});
</script>

<style scoped lang="scss">
.payment-sheet {
  color: #18181b;
}

.section-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #71717a;
  margin: 16px 2px 8px;

  &.first { margin-top: 4px; }
}

.method-card {
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

.method-row {
  border-bottom: 1px solid #f4f4f5;
  &.last { border-bottom: none; }
}

.method-head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 13px;
  cursor: pointer;
  background: #fff;
  transition: background 0.15s;
  &.on { background: #f5f4fe; }
}

.tile {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  i { font-size: 17px; }
}

.m-title {
  font-size: 14px;
  font-weight: 500;
  color: #18181b;
  &.sel { font-weight: 600; }
}
.m-sub {
  font-size: 11.5px;
  color: #71717a;
  margin-top: 2px;
}

.radio {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 2px solid #d4d4d8;
  background: #fff;
  box-sizing: border-box;
  flex: none;
  transition: border 0.15s;
  &.on { border: 6.5px solid #8864ee; }
}

.method-panel {
  padding: 12px 13px 14px;
  background: #fafafa;
  border-top: 1px solid #edeafd;
}

.ask {
  font-size: 12px;
  font-weight: 600;
  color: #3f3f46;
  margin-bottom: 9px;
}

.chips {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.chip {
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  cursor: pointer;
  font-weight: 500;
  background: #fff;
  color: #52525b;
  border: 1px solid #e4e4e7;
  transition: all 0.15s;
  &.on { background: #8864ee; color: #fff; border-color: #8864ee; font-weight: 600; }
}

.pick-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.pick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 11px;
  cursor: pointer;
  background: #fff;
  border: 1.5px solid #e4e4e7;
  transition: border-color 0.15s;
  &.on { border-color: #a691f4; }
}
.pick-radio {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  box-sizing: border-box;
  flex: none;
  border: 2px solid #d4d4d8;
  background: #fff;
  &.on { border: 5.5px solid #8864ee; }
}
.pick-name { font-size: 13px; font-weight: 600; color: #18181b; }
.pick-note { font-size: 11px; color: #71717a; margin-top: 2px; }

.empty-box {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  background: #fff;
  border: 1px dashed #d4d4d8;
  border-radius: 11px;
  padding: 11px 12px;
}
.empty-title { font-size: 12.5px; font-weight: 500; color: #52525b; }
.empty-hint { font-size: 11px; color: #a1a1aa; margin-top: 3px; }

.price-card {
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.18s;
  &.open { border-color: #ddd8fc; }
}
.price-head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 13px;
  cursor: pointer;
}
.price-tile {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #edeafd;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  i { color: #723be3; font-size: 16px; }
}
.p-title { font-size: 14px; font-weight: 500; color: #18181b; }
.gross { font-size: 15px; font-weight: 700; color: #18181b; direction: ltr; &.changed { color: #723be3; } }
.list-strike { font-size: 12px; color: #a1a1aa; text-decoration: line-through; direction: ltr; }
.price-sub { font-size: 11.5px; color: #71717a; margin-top: 3px; &.changed { color: #723be3; font-weight: 600; } }
.chev { color: #a1a1aa; font-size: 15px; }

.price-body {
  border-top: 1px solid #f4f4f5;
  background: #fafafa;
  padding: 4px 14px 13px;
}
.price-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f4f4f5;
  &.last { border-bottom: none; }
}
.pname { font-size: 13px; font-weight: 500; color: #27272a; line-height: 1.35; }
.pmeta { display: flex; align-items: center; gap: 7px; margin-top: 3px; }
.pmeta .list { font-size: 11px; color: #a1a1aa; }
.reset {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10.5px;
  font-weight: 600;
  color: #723be3;
  cursor: pointer;
  i { font-size: 10px; }
}
.price-input {
  position: relative;
  width: 96px;
  flex: none;
  .cur {
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: #a1a1aa;
    pointer-events: none;
    font-weight: 600;
    z-index: 1;
  }
  &.overridden .cur { color: #723be3; }
  :deep(.p-inputnumber),
  :deep(input) { width: 100%; }
}

.info-callout {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f5f4fe;
  border: 1px solid #edeafd;
  border-radius: 11px;
  padding: 9px 11px;
  margin-top: 12px;
  i { color: #723be3; font-size: 14px; flex: none; margin-top: 1px; }
  span { font-size: 11px; color: #52525b; line-height: 1.55; }
}

.visit-total {
  border-top: 1px solid #e4e4e7;
  padding: 12px 2px 4px;
}
.breakdown {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 8px;
}
.bd-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
}
.bd-label { color: #71717a; }
.bd-amt { direction: ltr; font-weight: 600; color: #52525b; &.neg { color: #15803d; } }
.total-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.total-label { font-size: 13.5px; font-weight: 600; color: #52525b; flex: 1; }
.total-strike { font-size: 12.5px; color: #a1a1aa; text-decoration: line-through; direction: ltr; }
.total-amt { font-size: 21px; font-weight: 700; letter-spacing: -0.02em; color: #18181b; direction: ltr; &.changed { color: #723be3; } }
</style>
